import { useCallback, useEffect, useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { meta } from "@/lib/metaPixel";
import {
  ArrowLeft,
  CheckCircle2,
  XCircle,
  Loader2,
  Plug,
  Trash2,
  ExternalLink,
  BookText,
  Database,
  ChevronRight,
} from "lucide-react";

const NOTION_HELP_URL =
  "https://www.notion.com/help/create-integrations-with-the-notion-api";

export default function Integrations() {
  const [loading, setLoading] = useState(true);
  const [status, setStatus] = useState(null);
  const [showAdvanced, setShowAdvanced] = useState(false);
  const [token, setToken] = useState("");
  const [dbUrl, setDbUrl] = useState("");
  const [saving, setSaving] = useState(false);
  const [testing, setTesting] = useState(false);
  const [oauthLoading, setOauthLoading] = useState(false);

  // Database picker (OAuth only)
  const [dbList, setDbList] = useState(null);
  const [dbListLoading, setDbListLoading] = useState(false);
  const [pickingId, setPickingId] = useState(null);

  const loc = useLocation();
  const nav = useNavigate();

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/integrations/notion");
      setStatus(data);
      if (data?.database_url) setDbUrl(data.database_url);
    } catch {
      setStatus({ connected: false, oauth_available: true });
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  // Handle ?notion=... on return from OAuth
  useEffect(() => {
    const params = new URLSearchParams(loc.search);
    const flag = params.get("notion");
    if (!flag) return;
    const clean = () => nav("/integrations", { replace: true });
    if (flag === "connected") { toast.success("Notion connected — pick a database below."); clean(); load(); }
    else if (flag === "denied") { toast.error("Notion authorization was denied."); clean(); }
    else if (flag === "expired") { toast.error("The connection window expired. Please try again."); clean(); }
    else if (flag === "exchange_failed") { toast.error("Notion rejected the authorization. Try connecting again."); clean(); }
    else if (flag === "missing_params") { toast.error("OAuth response missing parameters."); clean(); }
    else { toast.error("Notion connect error."); clean(); }
  }, [loc.search, load, nav]);

  const startOAuth = async () => {
    setOauthLoading(true);
    try {
      const { data } = await api.post("/integrations/notion/oauth/start", {
        origin: window.location.origin,
      });
      window.location.href = data.authorize_url;
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not start Notion OAuth.");
      setOauthLoading(false);
    }
  };

  const loadDatabases = useCallback(async () => {
    setDbListLoading(true);
    try {
      const { data } = await api.get("/integrations/notion/databases");
      setDbList(data.databases || []);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not fetch databases.");
      setDbList([]);
    } finally {
      setDbListLoading(false);
    }
  }, []);

  // Auto-load DB list when OAuth-connected but no DB picked yet
  useEffect(() => {
    if (status?.connected && status.needs_database_pick && dbList === null) {
      loadDatabases();
    }
  }, [status, dbList, loadDatabases]);

  const pickDatabase = async (id) => {
    setPickingId(id);
    try {
      await api.post("/integrations/notion/target-database", { database_id: id });
      // Meta: DMS_ChannelConnected fires only after the user has successfully
      // completed BOTH OAuth AND database selection — that's the point at
      // which the Notion integration is actually usable. We never transmit
      // the OAuth token, workspace id or database id itself.
      meta.channelConnected("Notion");
      toast.success("Target database set. Ready to send content.");
      setDbList(null);
      await load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not select database.");
    } finally {
      setPickingId(null);
    }
  };

  const onSave = async (e) => {
    e.preventDefault();
    if (!token.trim() || !dbUrl.trim()) {
      toast.error("Paste both the Notion token and the database URL.");
      return;
    }
    setSaving(true);
    try {
      const { data } = await api.post("/integrations/notion", {
        token: token.trim(),
        database_url: dbUrl.trim(),
      });
      if (data.ok) {
        // Meta: internal-token path also produces a live connection when the
        // save API reports `ok: true` (token accepted + database reachable).
        meta.channelConnected("Notion");
        toast.success(`Connected to “${data.database_title || "your Notion database"}”`);
      }
      else toast.error(data.error || "Connection test failed. Credentials saved — fix the issue and re-test.");
      setToken("");
      await load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not save Notion integration.");
    } finally {
      setSaving(false);
    }
  };

  const onTest = async () => {
    setTesting(true);
    try {
      const { data } = await api.post("/integrations/notion/test");
      if (data.ok) toast.success(`Notion reachable — “${data.database_title || "database"}”`);
      else toast.error(data.error || "Test failed.");
      await load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Test failed.");
    } finally {
      setTesting(false);
    }
  };

  const onDisconnect = async () => {
    if (!window.confirm("Disconnect Notion? Your token will be removed. Existing Notion pages stay in your workspace.")) return;
    try {
      await api.delete("/integrations/notion");
      toast.success("Notion disconnected.");
      setToken("");
      setDbUrl("");
      setDbList(null);
      await load();
    } catch {
      toast.error("Could not disconnect.");
    }
  };

  const authKind = status?.auth_kind || "internal";
  const isOAuth = status?.connected && authKind === "oauth";
  const needsDbPick = status?.connected && status.needs_database_pick;

  return (
    <div data-testid="integrations-page" className="px-5 sm:px-6 lg:px-10 py-8 sm:py-10 max-w-3xl">
      <Link to="/settings" data-testid="integrations-back" className="text-[13.5px] text-[#1D4E89] hover:text-[#0A2342] inline-flex items-center gap-1.5 mb-4">
        <ArrowLeft className="w-4 h-4" /> Back to Settings
      </Link>
      <div className="dms-label mb-3">Connect your tools</div>
      <h1 className="text-2xl sm:text-3xl lg:text-4xl font-heading font-bold text-[#0A2342]">Integrations</h1>
      <p className="mt-2 text-[#475569] text-[14.5px]">
        Push generated content straight into the tools you already use. Your credentials are encrypted at rest.
      </p>

      {/* Notion card */}
      <div className="mt-8 dms-card p-5 sm:p-6" data-testid="integration-notion-card">
        <div className="flex items-start justify-between gap-4">
          <div className="flex items-start gap-3">
            <div className="w-11 h-11 rounded-md bg-[#0A2342] text-white flex items-center justify-center font-bold text-lg">N</div>
            <div>
              <div className="font-heading font-bold text-[#0A2342] text-[16px]">Notion</div>
              <div className="text-[13px] text-[#64748B] mt-0.5">
                Send any Vault item to a database in your Notion workspace as a new page.
              </div>
            </div>
          </div>
          {loading ? (
            <span className="text-[12px] text-[#64748B] inline-flex items-center gap-1.5"><Loader2 className="w-3.5 h-3.5 animate-spin" /> Loading</span>
          ) : status?.connected ? (
            needsDbPick ? (
              <span data-testid="notion-status-badge" className="text-[12px] font-semibold text-[#1D4E89] bg-[#E0F2FE] rounded-full px-2.5 py-1 inline-flex items-center gap-1.5">
                <Database className="w-3.5 h-3.5" /> Pick a database
              </span>
            ) : status.last_test_ok ? (
              <span data-testid="notion-status-badge" className="text-[12px] font-semibold text-[#065F46] bg-[#D1FAE5] rounded-full px-2.5 py-1 inline-flex items-center gap-1.5">
                <CheckCircle2 className="w-3.5 h-3.5" /> Connected
              </span>
            ) : (
              <span data-testid="notion-status-badge" className="text-[12px] font-semibold text-[#92400E] bg-[#FEF3C7] rounded-full px-2.5 py-1 inline-flex items-center gap-1.5">
                <XCircle className="w-3.5 h-3.5" /> Needs attention
              </span>
            )
          ) : (
            <span data-testid="notion-status-badge" className="text-[12px] font-semibold text-[#64748B] bg-[#F1F5F9] rounded-full px-2.5 py-1 inline-flex items-center gap-1.5">
              <Plug className="w-3.5 h-3.5" /> Not connected
            </span>
          )}
        </div>

        {/* Connected summary */}
        {status?.connected && (
          <div className="mt-4 rounded-md bg-[#F8FAFC] border border-[#E2E8F0] p-3 text-[13px] text-[#334155]" data-testid="notion-connection-summary">
            {isOAuth && status.workspace_name ? (
              <div><span className="text-[#64748B]">Workspace:</span> <span className="font-semibold text-[#0A2342]">{status.workspace_icon ? status.workspace_icon + " " : ""}{status.workspace_name}</span></div>
            ) : null}
            {status.database_title ? (
              <div><span className="text-[#64748B]">Database:</span> <span className="font-semibold text-[#0A2342]">{status.database_title}</span></div>
            ) : needsDbPick ? (
              <div className="text-[#1D4E89]">Almost there — pick which database DMS should write to.</div>
            ) : null}
            {status.database_url ? (
              <a href={status.database_url} target="_blank" rel="noreferrer" className="text-[#1D4E89] hover:underline inline-flex items-center gap-1 text-[12.5px] mt-1">
                Open in Notion <ExternalLink className="w-3 h-3" />
              </a>
            ) : null}
            {status.last_error && !needsDbPick ? (
              <div className="mt-2 text-[#B91C1C] text-[12.5px]">{status.last_error}</div>
            ) : null}
          </div>
        )}

        {/* OAuth CTA (primary path) */}
        {!status?.connected && status?.oauth_available !== false && (
          <div className="mt-5">
            <button
              onClick={startOAuth}
              disabled={oauthLoading}
              data-testid="notion-oauth-btn"
              className="w-full sm:w-auto inline-flex items-center justify-center gap-2 rounded-md bg-[#0A2342] text-white px-5 py-3 text-[14px] font-semibold hover:bg-[#152F5A] disabled:opacity-60"
            >
              {oauthLoading ? <Loader2 className="w-4 h-4 animate-spin" /> : <span className="text-lg leading-none">N</span>}
              Connect with Notion
            </button>
            <p className="mt-2 text-[12px] text-[#64748B]">You&apos;ll pick your workspace and pages in Notion. Takes ~15 seconds.</p>
          </div>
        )}

        {/* Database picker (post-OAuth) */}
        {needsDbPick && (
          <div className="mt-5" data-testid="notion-database-picker">
            <div className="flex items-center justify-between mb-2">
              <div className="font-heading font-bold text-[#0A2342] text-[14px]">Choose your target database</div>
              <button onClick={loadDatabases} data-testid="notion-refresh-databases" className="text-[12.5px] text-[#1D4E89] hover:underline">Refresh</button>
            </div>
            {dbListLoading ? (
              <div className="text-[#64748B] text-sm inline-flex items-center gap-2"><Loader2 className="w-4 h-4 animate-spin" /> Loading databases you shared…</div>
            ) : dbList && dbList.length === 0 ? (
              <div className="rounded-md border border-dashed border-[#CBD5E1] bg-[#F8FAFC] p-4 text-[13px] text-[#475569]">
                No databases shared yet. In Notion, open the database you want, click <b>···</b> → <b>Connections</b> → add the DMS integration, then <button onClick={loadDatabases} className="text-[#1D4E89] font-semibold hover:underline">refresh</button>.
              </div>
            ) : (
              <ul className="space-y-2">
                {(dbList || []).map((d) => (
                  <li key={d.id}>
                    <button
                      data-testid={`notion-db-choice-${d.id}`}
                      onClick={() => pickDatabase(d.id)}
                      disabled={pickingId === d.id}
                      className="w-full text-left rounded-md border border-[#E2E8F0] hover:border-[#1D4E89] hover:bg-[#F8FAFC] p-3 flex items-center gap-3 transition"
                    >
                      <span className="text-lg">{d.icon || "📄"}</span>
                      <span className="flex-1">
                        <div className="font-semibold text-[#0A2342] text-[13.5px]">{d.title}</div>
                        <div className="text-[11.5px] text-[#94A3B8] truncate">{d.url}</div>
                      </span>
                      {pickingId === d.id ? <Loader2 className="w-4 h-4 animate-spin text-[#1D4E89]" /> : <ChevronRight className="w-4 h-4 text-[#94A3B8]" />}
                    </button>
                  </li>
                ))}
              </ul>
            )}
          </div>
        )}

        {/* Manage buttons for a fully-configured connection */}
        {status?.connected && !needsDbPick && (
          <div className="mt-5 flex flex-wrap items-center gap-2">
            {isOAuth ? (
              <>
                <button type="button" onClick={() => { setDbList(null); loadDatabases(); }} data-testid="notion-change-db-btn" className="dms-btn-ghost">
                  <Database className="w-4 h-4" /> Change database
                </button>
                <button type="button" onClick={onTest} disabled={testing} data-testid="notion-test-btn" className="dms-btn-ghost">
                  {testing ? (<><Loader2 className="w-4 h-4 animate-spin" /> Testing</>) : "Test connection"}
                </button>
              </>
            ) : (
              <button type="button" onClick={onTest} disabled={testing} data-testid="notion-test-btn" className="dms-btn-ghost">
                {testing ? (<><Loader2 className="w-4 h-4 animate-spin" /> Testing</>) : "Test connection"}
              </button>
            )}
            <button type="button" onClick={onDisconnect} data-testid="notion-disconnect-btn" className="ml-auto inline-flex items-center gap-2 rounded-md px-3 py-2 border border-[#FCA5A5] text-[#B91C1C] hover:bg-[#FEF2F2] text-[13px] font-semibold">
              <Trash2 className="w-3.5 h-3.5" /> Disconnect
            </button>
          </div>
        )}

        {/* Change-database picker for OAuth users (opens on demand) */}
        {isOAuth && !needsDbPick && dbList !== null && (
          <div className="mt-5" data-testid="notion-database-picker">
            <div className="flex items-center justify-between mb-2">
              <div className="font-heading font-bold text-[#0A2342] text-[14px]">Pick a different database</div>
              <button onClick={() => setDbList(null)} className="text-[12.5px] text-[#1D4E89] hover:underline">Cancel</button>
            </div>
            {dbListLoading ? (
              <div className="text-[#64748B] text-sm inline-flex items-center gap-2"><Loader2 className="w-4 h-4 animate-spin" /> Loading…</div>
            ) : (
              <ul className="space-y-2">
                {(dbList || []).map((d) => (
                  <li key={d.id}>
                    <button
                      data-testid={`notion-db-choice-${d.id}`}
                      onClick={() => pickDatabase(d.id)}
                      disabled={pickingId === d.id}
                      className={`w-full text-left rounded-md border p-3 flex items-center gap-3 transition ${d.id === status.database_id ? "border-[#1D4E89] bg-[#F1F5F9]" : "border-[#E2E8F0] hover:border-[#1D4E89] hover:bg-[#F8FAFC]"}`}
                    >
                      <span className="text-lg">{d.icon || "📄"}</span>
                      <span className="flex-1">
                        <div className="font-semibold text-[#0A2342] text-[13.5px]">{d.title}</div>
                        <div className="text-[11.5px] text-[#94A3B8] truncate">{d.url}</div>
                      </span>
                      {pickingId === d.id ? <Loader2 className="w-4 h-4 animate-spin text-[#1D4E89]" /> : <ChevronRight className="w-4 h-4 text-[#94A3B8]" />}
                    </button>
                  </li>
                ))}
              </ul>
            )}
          </div>
        )}

        {/* Advanced / internal-token flow */}
        {!status?.connected && (
          <div className="mt-6 border-t border-[#E2E8F0] pt-4">
            <button
              type="button"
              onClick={() => setShowAdvanced((v) => !v)}
              data-testid="notion-advanced-toggle"
              className="text-[12.5px] text-[#1D4E89] hover:underline inline-flex items-center gap-1.5"
            >
              <BookText className="w-3.5 h-3.5" /> Advanced: paste an Internal Integration Token instead
            </button>
            {showAdvanced && (
              <>
                <details className="mt-3" data-testid="notion-setup-help">
                  <summary className="cursor-pointer text-[13px] font-semibold text-[#0A2342]">
                    How to create an internal token (3 steps)
                  </summary>
                  <ol className="mt-3 pl-5 list-decimal text-[13.5px] text-[#334155] space-y-1.5">
                    <li>
                      Open Notion → Settings → <b>Connections</b> → <i>Develop your own connections</i>.
                      Create a new <b>Internal Integration</b>, then copy the token.{" "}
                      <a href={NOTION_HELP_URL} target="_blank" rel="noreferrer" className="text-[#1D4E89] hover:underline inline-flex items-center gap-1">
                        Notion guide <ExternalLink className="w-3 h-3" />
                      </a>
                    </li>
                    <li>Open the Notion database DMS should write to. Add at least a <b>Title</b> property (default).</li>
                    <li>In the database, click <b>···</b> → <b>Add connections</b> → select your integration. Then copy the database URL and paste it below.</li>
                  </ol>
                </details>
                <form onSubmit={onSave} className="mt-4 space-y-3">
                  <div>
                    <label className="text-[12.5px] font-semibold text-[#334155]">Notion Internal Integration Token</label>
                    <input
                      data-testid="notion-token-input"
                      type="password"
                      value={token}
                      onChange={(e) => setToken(e.target.value)}
                      placeholder="ntn_… or secret_…"
                      className="dms-input mt-1"
                      autoComplete="off"
                    />
                    <p className="text-[11px] text-[#94A3B8] mt-1">Stored encrypted. Only pushed to Notion at request time.</p>
                  </div>
                  <div>
                    <label className="text-[12.5px] font-semibold text-[#334155]">Target Notion Database URL</label>
                    <input
                      data-testid="notion-db-url-input"
                      value={dbUrl}
                      onChange={(e) => setDbUrl(e.target.value)}
                      placeholder="https://www.notion.so/workspace/DB-Name-abcdef1234567890..."
                      className="dms-input mt-1"
                    />
                  </div>
                  <div className="flex flex-wrap items-center gap-2 pt-1">
                    <button type="submit" disabled={saving} data-testid="notion-save-btn" className="dms-btn-primary">
                      {saving ? (<><Loader2 className="w-4 h-4 animate-spin" /> Saving</>) : "Connect with token"}
                    </button>
                  </div>
                </form>
              </>
            )}
          </div>
        )}
      </div>

      {/* Coming soon */}
      <div className="mt-4 dms-card p-5 sm:p-6 opacity-70" data-testid="integration-drive-card">
        <div className="flex items-start justify-between gap-4">
          <div className="flex items-start gap-3">
            <div className="w-11 h-11 rounded-md bg-[#FEF3C7] text-[#B45309] flex items-center justify-center font-bold text-lg">G</div>
            <div>
              <div className="font-heading font-bold text-[#0A2342] text-[16px]">Google Drive</div>
              <div className="text-[13px] text-[#64748B] mt-0.5">Auto-drop generated pieces into a Drive folder as DOCX.</div>
            </div>
          </div>
          <span className="text-[12px] font-semibold text-[#64748B] bg-[#F1F5F9] rounded-full px-2.5 py-1">Coming soon</span>
        </div>
      </div>
    </div>
  );
}
